import {
	DeviceProperties,
	EiaSubstationsProperties,
	EiaTransmissionProperties,
	LandownerProperties,
	Layer as LayerType,
	LayerSearchConfig,
	LoadProperties,
	PlannedPlantProperties,
	PlantProperties,
	TTMSProperties,
	UrlParams,
	UrlParamsConfig
} from '@/routes/map/types.ts';
import { GEOMAP_SOURCE_ID, YES_MAPBOX_SUBSTATIONS_ID, YES_MAPBOX_TRANSMISSION_ID } from '@/routes/map/constants.tsx';

function normalizeSearchText(value: string): string {
	return value
		.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();
}

export function featureMatchesSearchQuery(searchableFields: string[], query: string): boolean {
	const normalizedQuery = normalizeSearchText(query);

	if (!normalizedQuery) {
		return false;
	}

	const searchableText = normalizeSearchText(searchableFields.join(' '));

	if (searchableText.includes(normalizedQuery)) {
		return true;
	}

	return normalizedQuery.split(' ').every(token => searchableText.includes(token));
}

function getPropertyAsString(properties: LandownerProperties, keys: string[]): string {
	for (const key of keys) {
		const value = properties[key];

		if (value !== null && value !== undefined) {
			return value.toString();
		}
	}

	return '';
}

function formatLandownerName(properties: LandownerProperties): string {
	return [
		getPropertyAsString(properties, ['firstName', 'first_name', 'firstname', 'first']),
		getPropertyAsString(properties, ['lastName', 'last_name', 'lastname', 'last'])
	]
		.filter(Boolean)
		.join(' ')
		.trim();
}

export function parseUrlParams(): UrlParams | null {
	const urlParamsConfig: UrlParamsConfig[] = [
		{ key: 'lng', resultKey: 'longitude', type: 'number', min: -180, max: 180, required: true },
		{ key: 'lat', resultKey: 'latitude', type: 'number', min: -90, max: 90, required: true },
		{ key: 'zoom', resultKey: 'zoom', type: 'number', min: 0, max: 22, required: true },
		{ key: 'deviceId', resultKey: 'deviceId', type: 'number', min: 0, required: false }
	];

	const urlParams = new URLSearchParams(window.location.search);
	const result: UrlParams = {};

	for (const param of urlParamsConfig) {
		const value = urlParams.get(param.key);

		if (value === null) {
			if (param.required) return null;
			continue;
		}

		if (param.type === 'string') {
			if (param.options !== undefined && !param.options.includes(value)) {
				return null;
			}

			(result as Record<string, string | number>)[param.resultKey] = value;
		} else if (param.type === 'number') {
			const num = parseFloat(value);
			if (isNaN(num)) return null;

			if ((param.min !== undefined && num < param.min) || (param.max !== undefined && num > param.max)) {
				return null;
			}

			(result as Record<string, string | number>)[param.resultKey] = num;
		}
	}

	return result;
}

export const SEARCHABLE_LAYERS_CONFIG: Record<LayerType, LayerSearchConfig> = {
	[LayerType.Landowners]: {
		sourceId: GEOMAP_SOURCE_ID,
		sourceLayer: LayerType.Landowners,
		getName: (properties: LandownerProperties) => {
			return properties.name || formatLandownerName(properties) || 'Unknown Landowner';
		},
		getId: (properties: LandownerProperties) => {
			return properties.id;
		},
		getSearchableFields: (properties: LandownerProperties) => [
			properties.name?.toString() || '',
			formatLandownerName(properties),
			getPropertyAsString(properties, ['firstName', 'first_name', 'firstname', 'first']),
			getPropertyAsString(properties, ['lastName', 'last_name', 'lastname', 'last']),
			properties.status?.toString() || '',
			properties.id.toString()
		]
	},
	[LayerType.Devices]: {
		sourceId: GEOMAP_SOURCE_ID,
		sourceLayer: LayerType.Devices,
		getName: (properties: DeviceProperties) => {
			return `${properties.site ?? 'N/A'}`;
		},
		getId: (properties: DeviceProperties) => {
			return properties.id;
		},
		getSearchableFields: (properties: DeviceProperties) => [
			properties.name || '',
			properties.site || '',
			properties.siteId?.toString() || '',
			properties.landowner || '',
			properties.id.toString() || ''
		]
	},
	[LayerType.Plants]: {
		sourceId: GEOMAP_SOURCE_ID,
		sourceLayer: LayerType.Plants,
		getName: (properties: PlantProperties) => {
			return properties.name ?? 'N/A';
		},
		getId: (properties: PlantProperties) => {
			return properties.id;
		},
		getSearchableFields: (properties: PlantProperties) => [properties.name || '', properties.id.toString() || '']
	},
	[LayerType.Ttms]: {
		sourceId: GEOMAP_SOURCE_ID,
		sourceLayer: LayerType.Ttms,
		getName: (properties: TTMSProperties) => {
			return properties.name ?? 'N/A';
		},
		getId: (properties: TTMSProperties) => {
			return properties.id;
		},
		getSearchableFields: (properties: TTMSProperties) => [properties.id.toString() || '', properties.ttmsZone || '']
	},
	[LayerType.EiaTransmission]: {
		sourceId: YES_MAPBOX_TRANSMISSION_ID,
		sourceLayer: LayerType.EiaTransmission,
		getName: (properties: EiaTransmissionProperties) => {
			return properties.ID?.toString() || 'N/A';
		},
		getId: (properties: EiaTransmissionProperties) => {
			return Number(properties.ID) || 0;
		},
		getSearchableFields: (properties: EiaTransmissionProperties) => [
			properties.ID?.toString() || '',
			properties.TYPE?.toString() || '',
			properties.STATUS?.toString() || '',
			properties.OWNER?.toString() || '',
			properties.VOLT_CLASS?.toString() || '',
			properties.SUB_1?.toString() || '',
			properties.SUB_2?.toString() || ''
		]
	},
	[LayerType.EiaSubstations]: {
		sourceId: YES_MAPBOX_SUBSTATIONS_ID,
		sourceLayer: LayerType.EiaSubstations,
		getName: (properties: EiaSubstationsProperties) => {
			return properties.NAME || 'N/A';
		},
		getId: (properties: EiaSubstationsProperties) => {
			return Number(properties.ID) || 0;
		},
		getSearchableFields: (properties: EiaSubstationsProperties) => [
			properties.ID?.toString() || '',
			properties.NAME?.toString() || '',
			properties.TYPE?.toString() || '',
			properties.STATUS?.toString() || '',
			properties.LINES?.toString() || '',
			properties.MIN_VOLT?.toString() || '',
			properties.MAX_VOLT?.toString() || ''
		]
	},
	[LayerType.PlannedPlants]: {
		sourceId: GEOMAP_SOURCE_ID,
		sourceLayer: LayerType.PlannedPlants,
		getName: (properties: PlannedPlantProperties) => {
			return properties.name ?? 'N/A';
		},
		getId: (properties: PlannedPlantProperties) => {
			return properties.id;
		},
		getSearchableFields: (properties: PlannedPlantProperties) => [
			properties.id.toString() || '',
			properties.name || '',
			properties.primaryFuel || ''
		]
	},
	[LayerType.Loads]: {
		sourceId: GEOMAP_SOURCE_ID,
		sourceLayer: LayerType.Loads,
		getName: (properties: LoadProperties) => {
			return properties.name ?? 'N/A';
		},
		getId: (properties: LoadProperties) => {
			return properties.id;
		},
		getSearchableFields: (properties: LoadProperties) => [
			properties.id.toString() || '',
			properties.name || '',
			properties.facilityCategory || '',
			properties.powerCapacityMw?.toString() || ''
		]
	}
};
